import format from 'format-number';
import { capitalize, isNumber, reduce } from 'lodash-es';
import logger from '@/app/logger';
import type { ZodSchema } from 'zod';
import type { LocationQuery } from 'vue-router';
import { isAxiosError } from 'axios';

let lastId = 0;

export default {
  newId(): number {
    lastId++; // FIXME remove global state
    return lastId;
  },

  contactFormStrings: [
    {
      id: 'selling',
      action: 'Haluan arviokäynnin',
      question: 'Millaista kohdetta olet myymässä?',
      message: 'Hei, haluaisin hinta-arvion asunnostani:',
    },
    {
      id: 'buying',
      action: 'Olen ostamassa',
      question: 'Millaista kohdetta olet ostamassa?',
      message: 'Hei, olen ostamassa kohdetta',
    },
    {
      id: 'leaseWanted',
      action: 'Haluan vuokrata',
      question: 'Millaista kohdetta olet vuokraamassa?',
      message: 'Hei, olen vuokraamassa kohdetta',
    },
    {
      id: 'leaseOffered',
      action: 'Haluan antaa vuokralle',
      question: 'Millaisen kohteen haluat antaa vuokralle?',
      message: 'Hei, olen antamassa vuokralle kohdetta',
    },
    { id: 'office', action: 'Muu asia', question: '', message: '' },
  ],

  stripWhiteSpaces(value: string | number): string {
    return value.toString().replace(/\s+/g, '');
  },

  formatNumber(value: number | string, decimals: number = 0): string {
    const rounded = decimals >= 0 ? Number(value).toFixed(decimals) : value;
    const formatter = format({ integerSeparator: ' ', decimal: ',' });
    // @ts-expect-error not sure how formatter is intended to work, this is how it has always worked.
    return formatter(rounded);
  },

  inputFocusOnAndroid(event: FocusEvent): void {
    // workaround for Android Chrome bug: focused input is sometimes hidden behind the keyboard:
    // https://bugs.chromium.org/p/chromium/issues/detail?id=270018
    if (typeof navigator !== 'undefined' && navigator.userAgent.match(/android/i)) {
      // @ts-expect-error not sure what type casting should be used here. NOR that is this workaround even valid anymore?
      event.target?.parentNode?.scrollIntoView();
    }
  },

  getErrorCode(err: unknown): number {
    if (isAxiosError(err)) {
      return err.response && err.response.status && isNumber(err.response.status) ? err.response.status : 500;
    }

    return 500;
  },

  stripQuery(url: string): string {
    if (url.indexOf('?') === -1) {
      return url;
    }
    return url.substring(0, url.indexOf('?'));
  },

  splitPathToParts(path: string): string[] {
    path = this.stripQuery(path);
    return path.substring(path.indexOf('/') + 1).split('/');
  },

  nameToPathFormat(name: string): string {
    return name ? name.toLowerCase().replace(/ä/g, 'a').replace(/ö/g, 'o') : name;
  },

  capitalizeMultiPartWords(word?: string): string | undefined {
    return (
      word &&
      reduce(
        word.split('-'),
        (result, part, index) => (index === 0 ? capitalize(part) : `${result}-${capitalize(part)}`),
        ''
      )
    );
  },

  getMetaTagsForPage(
    title: string,
    description?: string,
    imageUrl?: string,
    noIndex: boolean = false
  ): Array<
    | {
        vmid: string;
        name: string;
        content: string;
        property?: undefined;
      }
    | {
        vmid: string;
        property: string;
        content: string;
        name?: undefined;
      }
  > {
    const tags = [
      { vmid: 'title', name: 'title', content: title },
      ...(description ? [{ vmid: 'description', name: 'description', content: description }] : []),
      { vmid: 'og:title', property: 'og:title', content: title },
      ...(description ? [{ vmid: 'og:description', property: 'og:description', content: description }] : []),
      ...(imageUrl ? [{ vmid: 'og:image', property: 'og:image', content: imageUrl }] : []),
      { vmid: 'twitter:title', property: 'twitter:title', content: title },
      ...(description ? [{ vmid: 'twitter:description', property: 'twitter:description', content: description }] : []),
      ...(imageUrl ? [{ vmid: 'twitter:image', property: 'twitter:image', content: imageUrl }] : []),
      { vmid: 'twitter:card', property: 'twitter:card', content: 'summary_large_image' },
      {
        vmid: 'google-site-verification',
        name: 'google-site-verification',
        content: 'IVmQji25SgCzkoCbTHsJgkHwX1R8F_rqx-Ees6KcMlE',
      },
    ];

    if (noIndex) {
      tags.push({ vmid: 'robots', name: 'robots', content: 'noindex' });
    }

    return tags.filter((entry) => !!entry.content);
  },

  parseRouteQuery(query: LocationQuery, schema: ZodSchema<any>): object | undefined {
    const result = schema.safeParse(query);
    if (result.error) {
      logger.info('Request url validation failed', result.error);
      return undefined;
    }
    return result.data;
  },

  setFocusForAccessibility(element: HTMLElement | undefined | null, focusOptions?: FocusOptions): void {
    if (element) {
      element.setAttribute('tabindex', '-1');
      element.focus(focusOptions);
    }
  },

  /* * Formats a number by adding spaces as thousands separators.
    Example: 1234567 -> "1 234 567"
  */
  formatNumberWithSpaces(num: number | string): string {
    return Number(num).toLocaleString('fi-FI');
  },

  formatCurrency(amount: number, minimumFractionDigits: number, maximumFractionDigits: number): string {
    return amount.toLocaleString('fi-FI', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits,
      maximumFractionDigits,
    });
  },
};
